import { useState, useEffect, useCallback } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { RefreshCw, Loader2, Phone, MessageSquare, Briefcase, Target, Clock, ChevronDown, ChevronUp, ShieldAlert } from "lucide-react";
import { getTeamUtilisation } from "@/lib/api";
import { toast } from "sonner";

const fmtGBP = (v) => new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP", maximumFractionDigits: 0 }).format(v || 0);
const fmtMins = (sec) => {
  const m = Math.floor((sec || 0) / 60);
  return m >= 60 ? `${Math.floor(m / 60)}h ${m % 60}m` : `${m}m`;
};

function HourlyStrip({ hourly }) {
  const max = Math.max(1, ...hourly);
  return (
    <div className="flex items-end gap-[2px]" title="Activity by hour (00–23, London time)">
      {hourly.map((v, h) => (
        <div key={h} className="flex flex-col items-center">
          <div
            className={`w-2 rounded-sm ${v > 0 ? "bg-red-500" : "bg-slate-200"}`}
            style={{ height: `${4 + (v / max) * 24}px`, opacity: v > 0 ? 0.35 + (v / max) * 0.65 : 1 }}
          />
          {h % 6 === 0 && <span className="text-[8px] text-muted-foreground mt-0.5">{String(h).padStart(2, "0")}</span>}
        </div>
      ))}
    </div>
  );
}

function MemberRow({ m }) {
  const [expanded, setExpanded] = useState(false);
  const hit = m.collections.hit_rate_pct;
  return (
    <Card className="border-slate-200" data-testid={`team-member-${m.name.toLowerCase().replace(/\s+/g, "-")}`}>
      <CardContent className="py-3 px-4">
        <div className="grid grid-cols-2 md:grid-cols-7 gap-3 items-center">
          <div className="md:col-span-2">
            <p className="font-semibold text-sm">{m.name}</p>
            <p className="text-xs text-muted-foreground">
              {m.book.assigned_cases} assigned • {fmtGBP(m.book.assigned_arrears)} arrears
            </p>
          </div>
          <div>
            <p className="text-[10px] text-muted-foreground uppercase flex items-center gap-1"><Phone className="w-3 h-3" /> Calls</p>
            <p className="text-sm font-bold">{m.calls.total}</p>
            <p className="text-[10px] text-muted-foreground">{m.calls.outbound} out / {m.calls.inbound} in • {fmtMins(m.calls.talk_time_sec)}</p>
          </div>
          <div>
            <p className="text-[10px] text-muted-foreground uppercase flex items-center gap-1"><MessageSquare className="w-3 h-3" /> SMS on book</p>
            <p className="text-sm font-bold">{m.sms_on_book}</p>
          </div>
          <div>
            <p className="text-[10px] text-muted-foreground uppercase flex items-center gap-1"><Target className="w-3 h-3" /> Hit rate</p>
            <Badge className={hit >= 10 ? "bg-green-100 text-green-700 border-green-300" : hit > 0 ? "bg-amber-100 text-amber-700 border-amber-300" : "bg-slate-100 text-slate-500 border-slate-200"}>
              {hit}%
            </Badge>
            <p className="text-[10px] text-muted-foreground mt-0.5">{m.collections.paid_cases} paid • {fmtGBP(m.collections.collected_amount)}</p>
          </div>
          <div className="md:col-span-2 flex items-center justify-between gap-2">
            <HourlyStrip hourly={m.hourly} />
            <Button variant="ghost" size="sm" className="h-7 px-2" onClick={() => setExpanded(!expanded)} data-testid={`expand-${m.name.toLowerCase().replace(/\s+/g, "-")}`}>
              {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </Button>
          </div>
        </div>
        {expanded && (
          <div className="mt-3 pt-3 border-t space-y-1 max-h-[300px] overflow-y-auto">
            {m.events.length === 0 ? (
              <p className="text-xs text-muted-foreground text-center py-3">No logged activity for this day</p>
            ) : (
              m.events.map((e, i) => (
                <div key={i} className="flex items-start gap-2 text-xs">
                  <span className="text-muted-foreground shrink-0 w-12 font-mono">
                    {e.at ? new Date(e.at).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" }) : "—"}
                  </span>
                  <Badge variant="outline" className="text-[9px] px-1 shrink-0">{e.type}</Badge>
                  <span className="truncate">{e.title}</span>
                </div>
              ))
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function TeamUtilisation() {
  const [date, setDate] = useState(new Date().toISOString().split("T")[0]);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [forbidden, setForbidden] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getTeamUtilisation(date);
      setData(res);
      setForbidden(false);
    } catch (err) {
      if (err?.response?.status === 403) setForbidden(true);
      else toast.error("Failed to load team utilisation");
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [date]);

  useEffect(() => { load(); }, [load]);

  if (forbidden) {
    return (
      <Card className="border-red-200 bg-red-50">
        <CardContent className="py-10 text-center">
          <ShieldAlert className="w-10 h-10 mx-auto text-red-500 mb-3" />
          <p className="text-red-700 font-semibold">Admins only</p>
          <p className="text-sm text-red-600">Team utilisation is reserved for administrators.</p>
        </CardContent>
      </Card>
    );
  }

  const totals = data?.team_totals;
  return (
    <div className="space-y-4" data-testid="team-utilisation-tab">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h3 className="font-semibold text-red-700 flex items-center gap-2">
            <Briefcase className="w-4 h-4" /> Team Utilisation
          </h3>
          <p className="text-xs text-muted-foreground">
            Calls, SMS, assigned arrears hit rate and hourly activity per team member
            {data?.data_freshness?.aircall_log_latest && (
              <> • Aircall log last synced {new Date(data.data_freshness.aircall_log_latest).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="w-40 h-9" data-testid="team-util-date" />
          <Button variant="outline" size="sm" onClick={load} disabled={loading}>
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16 text-muted-foreground">
          <Loader2 className="w-6 h-6 animate-spin mr-2" /> Building utilisation report...
        </div>
      ) : !data?.members?.length ? (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            <Clock className="w-10 h-10 mx-auto mb-3 opacity-30" />
            No team activity found for {new Date(date).toLocaleDateString("en-GB")}
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
            {[
              ["Team members", totals.members],
              ["Calls made", totals.calls],
              ["Talk time", fmtMins(totals.talk_time_sec)],
              ["SMS sent", totals.sms_sent],
              ["Assigned arrears", fmtGBP(totals.assigned_arrears)],
              ["Collected", fmtGBP(totals.collected_amount)],
            ].map(([label, value]) => (
              <Card key={label} className="bg-red-50/50 border-red-100">
                <CardContent className="py-2.5 px-3">
                  <p className="text-[10px] text-red-600 uppercase">{label}</p>
                  <p className="text-lg font-bold text-red-800">{value}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="space-y-2">
            {data.members.map((m) => <MemberRow key={m.name} m={m} />)}
          </div>

          {totals.unattributed_calls > 0 && (
            <p className="text-xs text-muted-foreground">
              {totals.unattributed_calls} calls that day had no agent attached (IVR / unanswered queue) and aren't attributed above.
            </p>
          )}
        </>
      )}
    </div>
  );
}
